import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, Space, Typography, message, Modal, Divider, Spin } from 'antd';
import { SaveOutlined, LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserDetail, updateUser, changePassword } from '../../api/users';

const { Title, Text } = Typography;
const { Option } = Select;

const UserDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);

    const { data: result, isLoading } = useQuery({
        queryKey: ['adminUserDetail', id],
        queryFn: () => getUserDetail(id!),
        enabled: !!id,
    });

    const user = result?.data;

    const updateMutation = useMutation({
        mutationFn: updateUser,
        onSuccess: () => {
            message.success('Kullanıcı bilgileri güncellendi.');
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', id] });
            queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
        },
        onError: () => message.error('Güncelleme başarısız.'),
    });

    const passwordMutation = useMutation({
        mutationFn: changePassword,
        onSuccess: () => {
            message.success('Şifre başarıyla değiştirildi.');
            setIsPasswordModalVisible(false);
            passwordForm.resetFields();
        },
        onError: () => message.error('Şifre değiştirme başarısız.'),
    });

    if (isLoading) return <div className="p-10 text-center"><Spin size="large" /></div>;

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/users')} />
                <Title level={2} className="mb-0">Kullanıcı Detayı</Title>
            </div>

            <Card className="shadow-sm">
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={user}
                    onFinish={(values) => updateMutation.mutate({ ...values, id: id! })}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                        <Form.Item name="firstName" label="Ad" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="lastName" label="Soyad" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </div>

                    <Form.Item label="E-posta">
                        <Input value={user?.email} disabled />
                        <Text type="secondary">E-posta adresi değiştirilemez.</Text>
                    </Form.Item>

                    <Form.Item name="role" label="Rol" rules={[{ required: true }]}>
                        <Select>
                            <Option value="User">User</Option>
                            <Option value="Admin">Admin</Option>
                            <Option value="SuperAdmin">SuperAdmin</Option>
                        </Select>
                    </Form.Item>

                    <Divider />

                    <div className="flex justify-between items-center">
                        <Space>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />} 
                                htmlType="submit" 
                                loading={updateMutation.isPending}
                            >
                                Kaydet
                            </Button>
                            <Button 
                                icon={<LockOutlined />} 
                                onClick={() => setIsPasswordModalVisible(true)}
                            >
                                Şifre Değiştir
                            </Button>
                        </Space>
                        <Text type="secondary">Kayıt: {new Date(user?.createdAt).toLocaleDateString()}</Text>
                    </div>
                </Form>
            </Card>

            <Modal
                title="Şifre Değiştir"
                open={isPasswordModalVisible}
                onOk={() => passwordForm.submit()}
                onCancel={() => setIsPasswordModalVisible(false)}
                confirmLoading={passwordMutation.isPending}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={(values) => passwordMutation.mutate({ id: id!, newPassword: values.newPassword })}
                >
                    <Form.Item 
                        name="newPassword" 
                        label="Yeni Şifre" 
                        rules={[{ required: true, min: 6, message: 'Şifre en az 6 karakter olmalıdır.' }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item 
                        name="confirmPassword" 
                        label="Şifreyi Onayla" 
                        dependencies={['newPassword']}
                        rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Şifreler uyuşmuyor!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserDetails;
